import React from 'react';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import { CopyIcon } from '../../common/components';

interface IProps {
  value: string;
  /** Extra classes for the wrapper, so a caller can control its own layout. */
  className?: string;
}

/**
 * A licence key rendered for reading and copying: truncated monospace text plus
 * a copy button that writes to the clipboard and confirms with a toast.
 *
 * Extracted verbatim from management-table.component.tsx, which is now its
 * only caller: Squarebit-issued keys used to have a presentation of their own
 * beside that table and now render as rows in it, so there is one way a
 * licence key looks on this page regardless of which server issued it.
 */
export const CopyableKey = ({ value, className = '' }: IProps) => {
  const { t } = useTranslation();
  return (
    <div
      className={`flex items-center justify-center gap-[8px] min-w-0 ${className}`}
    >
      <span className="font-mono text-[12px] tracking-[0.05em] text-content-secondary truncate">
        {value}
      </span>
      <button
        type="button"
        className="w-[28px] h-[28px] rounded-[7px] bg-surface-base border border-border-dark/[0.11] flex items-center justify-center shrink-0 cursor-pointer text-content-muted-alt shadow-[0_1px_3px_rgba(0,0,0,0.07),0_1px_2px_rgba(0,0,0,0.05)] transition-all hover:text-blue hover:border-blue hover:bg-surface-hover"
        onClick={() => {
          navigator.clipboard.writeText(value);
          toast.success(t('management.table.copySuccess'));
        }}
      >
        <CopyIcon />
      </button>
    </div>
  );
};
